import React, { useState, useEffect } from 'react'
import Modal from '../../../../components/Modal'
import * as api from '../../../../lib/api'
import { useToast } from '../../../../contexts/ToastContext'

/**
 * Модалка конвертации кастомных операций в стандартные типы.
 * 
 * Props:
 *  - rule: { ruleId, ruleName, ruleAmount, ruleType } | null
 *  - onClose: () => void
 *  - onConverted: (ruleId) => void — вызывается после успешной конвертации
 *  - salarySettings: object — текущие настройки (для сохранения после удаления правила)
 *  - setSalarySettings: fn
 *  - setSalaryDirty: fn
 *  - profileId: string
 */
export default function ConvertModal({
  rule,
  onClose,
  onConverted,
  salarySettings,
  setSalarySettings,
  setSalaryDirty,
  profileId,
}) {
  const toast = useToast()
  const [target, setTarget] = useState('returns')
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [executing, setExecuting] = useState(false)
  const [confirm, setConfirm] = useState(false)

  // При открытии — подсчитать операции
  useEffect(() => {
    if (!rule) return
    setTarget('returns')
    setPreview(null)
    setConfirm(false)
    setLoading(true)

    api.countCustomOperations(rule.ruleId)
      .then(data => setPreview(data))
      .catch(err => {
        console.error('Ошибка подсчёта операций:', err)
        setPreview({ count: 0, minDate: null, maxDate: null })
        toast.error('Не удалось подсчитать операции')
      })
      .finally(() => setLoading(false))
  }, [rule])

  const handleExecute = async () => {
    if (!rule) return
    setExecuting(true)
    try {
      const result = await api.convertCustomOperations(rule.ruleId, target)

      // Удаляем кастомное правило из настроек
      const updatedRules = (salarySettings.customRules || []).filter(r => r.id !== rule.ruleId)
      const updatedSettings = { ...salarySettings, customRules: updatedRules }
      setSalarySettings(updatedSettings)

      // Сохраняем в БД
      await api.updateSalaryConfig(updatedSettings, profileId)
      setSalaryDirty(false)

      const targetLabels = { acceptance: 'Приёмку', issued: 'Выдачу', returns: 'Возврат' }
      toast.success(`${result.converted} операций переведены в «${targetLabels[target]}»`)
      onConverted?.(rule.ruleId)
      onClose()
    } catch (err) {
      console.error('Ошибка конвертации:', err)
      toast.error('Ошибка конвертации: ' + (err.message || ''))
    } finally {
      setExecuting(false)
    }
  }

  return (
    <Modal
      isOpen={!!rule}
      onClose={() => { if (!executing) onClose() }}
      title="Перевести операции"
    >
      {rule && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '320px' }}>
          {/* Информация об источнике */}
          <div style={{
            padding: '14px 16px', borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))',
            border: '1px solid rgba(245,158,11,0.2)'
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 600, marginBottom: '4px' }}>ОТКУДА</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--warning)' }}>{rule.ruleName}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {rule.ruleType === 'per_item' ? `${rule.ruleAmount}₽ за шт.` : rule.ruleType === 'percent' ? `${rule.ruleAmount}%` : `${rule.ruleAmount}₽ фикс.`}
                </div>
              </div>
            </div>
          </div>

          {/* Статистика */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
              <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
              </div>
              Подсчитываем записи...
            </div>
          ) : preview && (
            <div style={{
              padding: '14px 16px', borderRadius: '12px',
              background: 'var(--surface-hover)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--text-primary)' }}>
                {preview.count}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                {preview.count === 0 ? 'записей не найдено' : (
                  <>записей будет переведено<br />
                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                      {preview.minDate && `с ${new Date(preview.minDate).toLocaleDateString('ru-RU')} по ${new Date(preview.maxDate).toLocaleDateString('ru-RU')}`}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Выбор целевого типа */}
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 600, marginBottom: '8px' }}>КУДА ПЕРЕВЕСТИ</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { id: 'returns', title: 'Возврат', color: '#3B82F6', desc: 'Учитывается как возвраты, оплата по ставке «За возврат товара»' },
                { id: 'acceptance', title: 'Приёмка', color: '#14B8A6', desc: 'Учитывается как принятые товары, оплата по ставке «За принятый товар»' },
                { id: 'issued', title: 'Выдача', color: '#10B981', desc: 'Учитывается как выданные товары, оплата по ставке «За выданный товар»' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => { setTarget(opt.id); setConfirm(false) }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 16px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                    textAlign: 'left', transition: 'all 0.2s',
                    background: target === opt.id
                      ? `color-mix(in srgb, ${opt.color} 12%, transparent)`
                      : 'var(--surface)',
                    outline: target === opt.id
                      ? `2px solid ${opt.color}`
                      : '1px solid var(--surface-border)',
                    outlineOffset: '-1px',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: target === opt.id ? opt.color : 'var(--text-primary)' }}>{opt.title}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px', lineHeight: 1.3 }}>{opt.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Предупреждение */}
          {preview && preview.count > 0 && (
            <div style={{
              padding: '10px 14px', borderRadius: '10px',
              background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
              fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg> После перевода операции будут оплачиваться по ставке нового типа, а кастомное правило «{rule.ruleName}» будет удалено.
              Убедитесь, что нужная ставка уже настроена в тарифах выше.
            </div>
          )}

          {/* Кнопки */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ flex: 1 }}
              onClick={onClose}
              disabled={executing}
            >Отмена</button>

            {!confirm ? (
              <button
                type="button"
                className="btn btn-primary"
                style={{ flex: 2, background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                disabled={loading || !preview || preview.count === 0}
                onClick={() => setConfirm(true)}
              >
                Перевести {preview?.count || 0} операций
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                style={{ flex: 2, background: 'linear-gradient(135deg, #EF4444, #DC2626)', animation: 'pulse 1.5s infinite' }}
                disabled={executing}
                onClick={handleExecute}
              >
                {executing ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg> Выполняется...</> : 'Подтвердить перевод'}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
